import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { HELP_TOPICS } from "./helpKnowledge.js";
import { actionCatalogForRole, runOperatorAction, summarizeAction } from "./operatorActions.js";
import { masterSupabase } from "../lib/supabase.js";

// ═══ HelpFlyout v2 — the ASK ME bar is now the Operator ═══
// Same black bar, new powers: a conversation thread that answers questions
// (grounded in helpKnowledge), jumps to pages, and — when asked to DO
// something — proposes an action for the user to confirm. Execution happens
// in operatorActions.js as the logged-in user; the model only proposes.
// If the operator endpoint is unreachable, falls back to the old local
// keyword answers, so the guide never goes dark.

const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";
const C = { ink: "#1a1a1a", dim: "#8a8a8a", faint: "#c0bdb8", line: "#e5e3de", card: "#fff", accent: "#6366f1", green: "#16a34a", red: "#dc2626" };

function scoreTopic(topic, query) {
  const q = query.toLowerCase().trim();
  if (!q) return 0;
  const words = q.split(/\s+/).filter((w) => w.length >= 3);
  if (words.length === 0) return 0;
  let score = 0;
  for (const w of words) {
    for (const k of topic.keywords) {
      if (k === w) score += 6;
      else if (k.includes(w) || w.includes(k)) score += 3;
    }
    if (topic.title.toLowerCase().includes(w)) score += 2;
    if (topic.body.toLowerCase().includes(w)) score += 1;
  }
  for (const k of topic.keywords) if (q.includes(k) && k.includes(" ")) score += 5;
  return score;
}

function localFallback(query) {
  const best = HELP_TOPICS.map((t) => ({ t, s: scoreTopic(t, query) })).filter((x) => x.s > 0).sort((a, b) => b.s - a.s)[0];
  if (!best) return { kind: "answer", text: "I couldn't reach the assistant and nothing local matched. Try the page list below, or ask again in a moment." };
  return { kind: "answer", text: `${best.t.title}. ${best.t.body}`, route: best.t.route, local: true };
}

const KNOWLEDGE = HELP_TOPICS.map(({ route, page, title, body }) => ({ route, page, title, body }));

export default function HelpFlyout() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [thread, setThread] = useState([]);   // {who:"you"|"op", text, route?, action?, status?}
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 80); }, [open]);
  useEffect(() => { setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 60); }, [thread, busy]);

  const go = (route) => { navigate(route); };

  const ask = async () => {
    const message = input.trim();
    if (!message || busy) return;
    setInput("");
    const newThread = [...thread, { who: "you", text: message }];
    setThread(newThread);
    setBusy(true);
    try {
      const { data: { session } } = await masterSupabase.auth.getSession();
      const res = await fetch("/api/operator", {
        method: "POST",
        headers: { Authorization: `Bearer ${session?.access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
          route: location.pathname,
          history: newThread.slice(-9, -1).map(({ who, text }) => ({ who, text })),
          knowledge: KNOWLEDGE,
          actions: actionCatalogForRole(),
        }),
      });
      const json = await res.json().catch(() => ({}));
      const d = res.ok && json.data ? json.data : localFallback(message);
      if (d.kind === "action") {
        setThread((t) => [...t, { who: "op", text: d.summary || summarizeAction(d.tool, d.args), action: { tool: d.tool, args: d.args }, status: "proposed" }]);
      } else {
        setThread((t) => [...t, { who: "op", text: d.text, route: d.route }]);
      }
    } catch (e) {
      const d = localFallback(message);
      setThread((t) => [...t, { who: "op", text: d.text, route: d.route }]);
    }
    setBusy(false);
  };

  const confirmAction = async (idx) => {
    const item = thread[idx];
    if (!item?.action || item.status !== "proposed" || busy) return;
    setThread((t) => t.map((m, i) => (i === idx ? { ...m, status: "running" } : m)));
    setBusy(true);
    try {
      const result = await runOperatorAction(item.action.tool, item.action.args);
      setThread((t) => t.map((m, i) => (i === idx ? { ...m, status: "done" } : m)).concat([{ who: "op", text: result }]));
    } catch (e) {
      setThread((t) => t.map((m, i) => (i === idx ? { ...m, status: "proposed" } : m)).concat([{ who: "op", text: `Couldn't do that: ${e.message}` }]));
    }
    setBusy(false);
  };

  const cancelAction = (idx) => setThread((t) => t.map((m, i) => (i === idx ? { ...m, status: "cancelled" } : m)));

  const hasThread = thread.length > 0;

  return (
    <>
      {/* Launcher — the ASK ME bar, docked to the right edge */}
      <button onClick={() => setOpen((o) => !o)} title="Ask how anything works — or ask for something to be done"
        style={{ position: "fixed", top: "50%", right: 0, transform: "translateY(-50%)", width: 34, padding: "18px 0", border: "none", borderRadius: "10px 0 0 10px", background: C.ink, color: "#fff", cursor: "pointer", zIndex: 1200, boxShadow: "-3px 3px 14px rgba(0,0,0,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ writingMode: "vertical-rl", textOrientation: "mixed", fontFamily: sans, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase" }}>
          {open ? "Close" : "Ask me"}
        </span>
      </button>

      {/* Drawer */}
      {open && (
        <div style={{ position: "fixed", top: 0, right: 34, bottom: 0, width: 390, background: C.card, borderLeft: `1px solid ${C.line}`, boxShadow: "-12px 0 40px rgba(0,0,0,0.12)", zIndex: 1199, display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: sans, color: C.ink }}>
          <div style={{ padding: "18px 20px 10px", borderBottom: `1px solid ${C.line}` }}>
            <div style={{ fontFamily: serif, fontStyle: "italic", fontSize: 20 }}>What can I do for you?</div>
            <div style={{ fontSize: 11.5, color: C.dim, marginTop: 3, lineHeight: 1.5 }}>
              Ask how anything works — or ask me to do it. Actions always wait for your confirmation.
            </div>
          </div>

          {/* Thread / browse */}
          <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
            {!hasThread && (
              <>
                <div style={{ padding: "2px 8px 8px", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.faint }}>Try</div>
                {["Mark the lead from yesterday as contacted", "Run the compliance agent", "How does the retention policy work?", "Dismiss the duplicate finding for Quadratec"].map((ex) => (
                  <button key={ex} onClick={() => setInput(ex)} style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: `1px solid ${C.line}`, padding: "8px 11px", cursor: "pointer", fontFamily: sans, borderRadius: 8, marginBottom: 6, fontSize: 12, color: "#555" }}>
                    "{ex}"
                  </button>
                ))}
                <div style={{ padding: "12px 8px 8px", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.faint }}>Browse by page</div>
                {HELP_TOPICS.map((t) => (
                  <button key={t.route} onClick={() => { go(t.route); }}
                    style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "7px 8px", cursor: "pointer", fontFamily: sans, borderRadius: 8 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f6f5f2"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "none"}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: C.ink }}>{t.page}</span>
                    <span style={{ fontSize: 11.5, color: C.dim }}> — {t.title.toLowerCase().replace(/^the /, "")}</span>
                  </button>
                ))}
              </>
            )}

            {thread.map((m, i) => (
              <div key={i} style={{ marginBottom: 10, display: "flex", justifyContent: m.who === "you" ? "flex-end" : "flex-start" }}>
                <div style={{ maxWidth: "88%" }}>
                  <div style={{
                    padding: "9px 13px", borderRadius: m.who === "you" ? "12px 12px 3px 12px" : "12px 12px 12px 3px",
                    background: m.who === "you" ? C.ink : "#f6f5f2", color: m.who === "you" ? "#fff" : C.ink,
                    fontSize: 12.5, lineHeight: 1.6, whiteSpace: "pre-wrap",
                    border: m.action ? `1px solid ${m.status === "done" ? "#bbf7d0" : m.status === "cancelled" ? C.line : "#c7d2fe"}` : "none",
                  }}>
                    {m.action && <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: m.status === "done" ? C.green : m.status === "cancelled" ? C.faint : C.accent, marginBottom: 5 }}>
                      {m.status === "done" ? "✓ Done" : m.status === "cancelled" ? "Cancelled" : m.status === "running" ? "Working…" : "Proposed action"}
                    </div>}
                    {m.text}
                    {m.action && m.status === "proposed" && (
                      <div style={{ display: "flex", gap: 8, marginTop: 9 }}>
                        <button onClick={() => confirmAction(i)} disabled={busy} style={{ padding: "6px 14px", background: C.ink, color: "#fff", border: "none", borderRadius: 7, fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: sans }}>Confirm</button>
                        <button onClick={() => cancelAction(i)} style={{ padding: "6px 14px", background: "none", color: C.dim, border: `1px solid ${C.line}`, borderRadius: 7, fontSize: 11.5, cursor: "pointer", fontFamily: sans }}>Cancel</button>
                      </div>
                    )}
                    {m.route && !m.action && (
                      <div style={{ marginTop: 7 }}>
                        <button onClick={() => go(m.route)} style={{ background: "none", border: "none", color: C.accent, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: sans }}>
                          Take me there →
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {busy && <div style={{ fontSize: 11.5, color: C.faint, padding: "2px 6px" }}>thinking…</div>}
            <div ref={endRef} />
          </div>

          {/* Composer */}
          <div style={{ padding: "10px 14px 14px", borderTop: `1px solid ${C.line}`, display: "flex", gap: 8 }}>
            <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
              placeholder={hasThread ? "Reply…" : "Ask a question, or ask me to do something"}
              style={{ flex: 1, padding: "10px 13px", border: `1px solid ${C.line}`, borderRadius: 9, fontSize: 13, outline: "none", fontFamily: sans, boxSizing: "border-box" }} />
            <button onClick={ask} disabled={busy || !input.trim()} style={{ padding: "0 16px", background: C.ink, color: "#fff", border: "none", borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: busy || !input.trim() ? "not-allowed" : "pointer", fontFamily: sans, opacity: busy || !input.trim() ? 0.5 : 1 }}>→</button>
          </div>
        </div>
      )}
    </>
  );
}
